import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { makeGridPopupContainer } from '../../containers/GridPopup/GridPopup';
import { Bem } from '../../service/Bem/Bem';
import { getIconForStatus, getTitleForStatus } from '../../service/GridHelpers/GridHelpers';
import { BasePopupProps } from '../../typings/common';
import { AggregatedStatusInfoCounters } from '../../typings/domain';
import { GdSelfStatusEnum, GdSelfStatusReason } from '../../typings/graphql-grid';
import { PopupDecorator } from '../PopupDecorator/PopupDecorator';

import './GridESSStatusCell.css';
import * as keyset from './GridESSStatusCell.i18n';

const i18n = i18nFactory(keyset);

type GridESSStatusCellProps = {
    status: GdSelfStatusEnum;
    reasons: GdSelfStatusReason[];
    counters: Record<string, AggregatedStatusInfoCounters | undefined>;
};

const BLOCK = 'grid-ess-status-cell';
const titleChild = {
    ads: i18n('title-ads'),
    keywords: i18n('title-keywords'),
    adgroups: i18n('title-groups')
};

class GridESSStatusCell extends React.PureComponent<GridESSStatusCellProps & BasePopupProps> {
    private popupAnchor = React.createRef<HTMLDivElement>();

    render() {
        const { status, openPopup, reasons: reasonsList } = this.props;
        const title = getTitleForStatus(status);
        const hasPopup = this.isCountersPopupVisible();
        const reasons = reasonsList.map((reason, index) => <div key={index}>{reason}</div>);

        return (
            <div className={Bem.cls(BLOCK)}>
                <div
                    className={Bem.cls({
                        block: BLOCK,
                        elem: 'status',
                        mods: {
                            type: Bem.beminize(status),
                            hasPopup
                        }
                    })}
                    ref={this.popupAnchor}
                    onClick={hasPopup ? openPopup : undefined}
                >
                    {getIconForStatus(status, title, Bem.cls(BLOCK, 'icon'))}
                    {title}
                </div>
                <div
                    className={Bem.cls({
                        block: BLOCK,
                        elem: 'reasons'
                    })}
                >
                    {reasons}
                </div>
                {hasPopup && this.getPopup()}
            </div>
        );
    }

    private isCountersPopupVisible = () => {
        const { counters } = this.props;

        return Object.keys(counters).some(childKey => {
            const childValue = counters[childKey];

            if (childValue) {
                return Object.keys(childValue).length > 0;
            }

            return false;
        });
    };

    private getPopup = () => {
        const { isPopupVisible, closePopup, status } = this.props;
        const title = getTitleForStatus(status);

        return (
            <PopupDecorator
                cls={Bem.cls(BLOCK, 'popup')}
                onClose={closePopup}
                target={'anchor'}
                anchor={this.popupAnchor.current}
                directions={['right-center', 'right-top', 'right-bottom', 'left-center']}
                visible={isPopupVisible}
                hasTail={true}
                onOutsideClick={closePopup}
                header={title}
            >
                <div className={Bem.cls(BLOCK, 'popup-content')}>{this.getPopupContent()}</div>
            </PopupDecorator>
        );
    };

    private getPopupContent = () => {
        const { counters } = this.props;

        return Object.keys(counters).map((childKey: string, indexKey: number) => {
            const childValue = counters[childKey];

            if (childValue === undefined) {
                return [];
            }

            const childList = Object.keys(childValue).map((childStatus: GdSelfStatusEnum, indexStatus: number) => {
                const title = getTitleForStatus(childStatus);
                const hasLink = true;
                const key = `${indexKey}-${indexStatus}`;
                const countChildByStatus = childValue[childStatus];
                const childTitleByKey = {
                    ads: i18n('ads', { count: countChildByStatus }),
                    keywords: i18n('keywords', { count: countChildByStatus }),
                    adgroups: i18n('groups', { count: countChildByStatus })
                };

                return (
                    <div
                        className={Bem.cls({
                            block: BLOCK,
                            elem: 'status',
                            mods: {
                                type: Bem.beminize(childStatus),
                                child: true,
                                hasLink
                            }
                        })}
                        key={key}
                    >
                        <div
                            className={Bem.cls({
                                block: BLOCK,
                                elem: 'status-content'
                            })}
                        >
                            {getIconForStatus(childStatus, title, Bem.cls(BLOCK, 'icon'))}
                            {title}
                        </div>
                        <div
                            className={Bem.cls({
                                block: BLOCK,
                                elem: 'count-child'
                            })}
                        >
                            {childTitleByKey[childKey]}
                        </div>
                    </div>
                );
            });

            return (
                <div className={Bem.cls(BLOCK, 'child-container')} key={indexKey}>
                    <div className={Bem.cls(BLOCK, 'child-title')}>{titleChild[childKey]}</div>
                    {childList}
                </div>
            );
        });
    };
}

export const GridESSStatusCellContainer = makeGridPopupContainer<GridESSStatusCellProps>(GridESSStatusCell);
